import { connectWebSocket, send, on, isConnected, onServerReconnect } from '../serverClient';

// --- Internal state ---

let currentRoomCode = null;
let isHost = false;

// Game action handlers: Map<event, Set<callback>>
const actionHandlers = new Map();
// Connection event handlers
const playerJoinedHandlers = new Set();
const playerLeftHandlers = new Set();
const opponentDroppedHandlers = new Set();

// --- Subscriptions to serverClient events ---

let roomJoinedUnsub = null;
let roomResumedUnsub = null;
let opponentJoinedUnsub = null;
let opponentReconnectedUnsub = null;
let opponentDisconnectedUnsub = null;
let roomClosedUnsub = null;
let opponentLeftUnsub = null;
let roomErrorUnsub = null;
let gameActionUnsub = null;
let matchSettledUnsub = null;
let matchDisputeRepromptUnsub = null;

// Pending join promise
let joinResolver = null;
let joinRejecter = null;

function setupSubscriptions() {
  // Avoid double-subscribing
  if (roomJoinedUnsub) return;

  roomJoinedUnsub = on('room:joined', (data) => {
    currentRoomCode = data?.roomCode || currentRoomCode;
    isHost = !!data?.isHost;
    if (joinResolver) {
      joinResolver({ ...data, roomCode: currentRoomCode, resumed: false });
      joinResolver = null;
      joinRejecter = null;
    }
    if (data?.opponentInRoom) {
      for (const cb of playerJoinedHandlers) cb({});
    }
  });

  roomResumedUnsub = on('room:resumed', (data) => {
    currentRoomCode = data?.roomCode || currentRoomCode;
    isHost = !!data?.isHost;
    if (joinResolver) {
      joinResolver({ ...data, roomCode: currentRoomCode, resumed: true });
      joinResolver = null;
      joinRejecter = null;
    }
    if (data?.opponentInRoom) {
      for (const cb of playerJoinedHandlers) cb({});
    }
  });

  opponentJoinedUnsub = on('opponent:joined', () => {
    for (const cb of playerJoinedHandlers) cb({});
  });

  opponentReconnectedUnsub = on('opponent:reconnected', () => {
    for (const cb of playerJoinedHandlers) cb({});
  });

  opponentDisconnectedUnsub = on('opponent:disconnected', (data) => {
    // Temporary disconnect. Opponent may reconnect within the grace period.
    // Fire the dropped handlers (shows a waiting message), NOT playerLeft.
    for (const cb of opponentDroppedHandlers) cb(data || {});
  });

  roomClosedUnsub = on('room:closed', () => {
    // Permanent. Room destroyed (grace period expired or explicit leave).
    currentRoomCode = null;
    for (const cb of playerLeftHandlers) cb({ permanent: true });
  });

  // Draft-round graceful leave: opponent exited via Save & Leave
  // but the room is still alive so either side can rejoin. Fire
  // the same handler with a non-permanent flag. GameBoard routes
  // back to the tournament screen without destroying the room.
  opponentLeftUnsub = on('opponent:left', (data) => {
    for (const cb of playerLeftHandlers) cb({ permanent: false, ...data });
  });

  roomErrorUnsub = on('room:error', (data) => {
    if (joinRejecter) {
      joinRejecter(new Error(data?.error || 'Failed to join room'));
      joinResolver = null;
      joinRejecter = null;
    }
    console.warn('[socketClient] room:error', data);
  });

  gameActionUnsub = on('game:action', (data) => {
    if (!data?.event) return;
    const list = actionHandlers.get(data.event);
    if (list) {
      for (const cb of list) {
        try { cb(data); } catch (err) { console.error(err); }
      }
    }
  });

  // Server-pushed arbitration settlement. Sent as a top-level WS
  // message ({ type: 'match:settled' }) rather than wrapped in a
  // game:action envelope, so the gameActionUnsub above doesn't see
  // it. Bridge it into the same actionHandlers map so consumers
  // like GameBoard subscribe in one place.
  matchSettledUnsub = on('match:settled', (data) => {
    const list = actionHandlers.get('match:settled');
    if (list) {
      for (const cb of list) {
        try { cb(data); } catch (err) { console.error(err); }
      }
    }
  });

  // Server-pushed reprompt: both clients reported "I won", so reports
  // were wiped and the picker must re-open on both sides.
  matchDisputeRepromptUnsub = on('match:dispute_reprompt', (data) => {
    const list = actionHandlers.get('match:dispute_reprompt');
    if (list) {
      for (const cb of list) {
        try { cb(data); } catch (err) { console.error(err); }
      }
    }
  });

}

function teardownSubscriptions() {
  for (const unsub of [
    roomJoinedUnsub, roomResumedUnsub, opponentJoinedUnsub,
    opponentReconnectedUnsub, opponentDisconnectedUnsub,
    roomClosedUnsub, opponentLeftUnsub, roomErrorUnsub, gameActionUnsub,
    matchSettledUnsub, matchDisputeRepromptUnsub,
  ]) {
    if (unsub) {
      try { unsub(); } catch {}
    }
  }
  roomJoinedUnsub = roomResumedUnsub = opponentJoinedUnsub = null;
  opponentReconnectedUnsub = opponentDisconnectedUnsub = null;
  roomClosedUnsub = opponentLeftUnsub = roomErrorUnsub = gameActionUnsub = null;
  matchSettledUnsub = null;
  matchDisputeRepromptUnsub = null;
}

// --- Public API (matches PeerJS surface) ---

export async function createRoom() {
  // The new server doesn't support ad-hoc room creation outside matchmaking/invites.
  // Kept as a no-op stub for backwards compat.
  await connectWebSocket();
  setupSubscriptions();
  isHost = true;
  console.warn('[socketClient] createRoom() is deprecated. Rooms are created via matchmaking or friend invites');
  return null;
}

// One join attempt with a timeout scoped to THAT attempt. The old inline
// timers checked only "is ANY joinRejecter set", so a leftover 10s timer from
// the PREVIOUS match's (long-resolved) join fired mid-way through the NEXT
// match's in-flight join and rejected it as a phantom 'Join timeout'. The
// intermittent leave-then-start-within-10s "connection failed" brick. Each
// timer now only fires for its own attempt, and a settled attempt clears it.
function joinWithTimeout(sendType, code, timeoutLabel) {
  return new Promise((resolve, reject) => {
    let timer = null;
    joinResolver = (value) => { clearTimeout(timer); resolve(value); };
    joinRejecter = (err) => { clearTimeout(timer); reject(err); };
    const myRejecter = joinRejecter;
    send(sendType, { roomCode: code });
    timer = setTimeout(() => {
      if (joinRejecter === myRejecter) {
        joinRejecter(new Error(timeoutLabel));
        joinResolver = null;
        joinRejecter = null;
      }
    }, 10000);
  });
}

export async function createRoomWithCode(code) {
  await connectWebSocket();
  setupSubscriptions();
  return joinWithTimeout('room:join', code, 'Join timeout');
}

export async function joinRoom(code) {
  await connectWebSocket();
  setupSubscriptions();
  return joinWithTimeout('room:join', code, 'Join timeout');
}

export async function spectateRoom(code) {
  await connectWebSocket();
  setupSubscriptions();
  return joinWithTimeout('room:spectate', code, 'Spectate timeout');
}

export function disconnectSocket() {
  if (currentRoomCode) {
    // Name the room we mean to leave. The server pre-binds a player to a
    // freshly provisioned room at CREATE time (before the join), so an
    // anonymous leave that gets processed late. Right after the next
    // match's instance create. Would resolve to and destroy the NEW room
    // (the leave-then-start "Room not found" race). The server ignores a
    // leave whose code no longer matches the player's current room.
    send('room:leave', { roomCode: currentRoomCode });
  }
  currentRoomCode = null;
  isHost = false;
  teardownSubscriptions();
  // Clear handler sets too. These are module-level and persist
  // across GameBoard remounts. Without clearing, the unmounted
  // GameBoard's onStateSync callback stays registered and fires
  // on the next match's state:sync, crashing in restoreSession()
  // because its scene is disposed.
  playerJoinedHandlers.clear();
  playerLeftHandlers.clear();
  opponentDroppedHandlers.clear();
  actionHandlers.clear();
}

export function emitGameAction(event, data = {}) {
  if (!isConnected()) return;
  send('game:action', { event, ...data });
}

export function onGameAction(event, callback) {
  if (!actionHandlers.has(event)) actionHandlers.set(event, new Set());
  actionHandlers.get(event).add(callback);
}

export function offGameAction(event, callback) {
  const set = actionHandlers.get(event);
  if (set) set.delete(callback);
}

export function onPlayerJoined(callback) {
  playerJoinedHandlers.add(callback);
}

export function onPlayerLeft(callback) {
  playerLeftHandlers.add(callback);
}

export function onOpponentDropped(callback) {
  opponentDroppedHandlers.add(callback);
}

// Server-authored combat playback (events + per-seat redacted log), broadcast
// alongside the match:diff whenever a fight resolves.
export function onMatchCombat(callback) {
  return on('match:combat', (data) => callback(data));
}

// Server-authoritative command pipeline (Phase 1). The server reduces a
// `match:command` and broadcasts the resulting versioned wire message:
// `match:diff` for the incremental patch, `match:full` for a per-seat
// projection (the gap-recovery answer to a `match:full:request`). The
// payload `data` is the wire message ClientMirror.applyInbound consumes
// verbatim (`{ kind, version, patches }` or `{ kind, version, state }`).
export function onMatchDiff(callback) {
  return on('match:diff', (data) => callback(data));
}

export function onMatchFull(callback) {
  return on('match:full', (data) => callback(data));
}

// Server declined an intent (cast/move on the wrong turn, out-of-reach move,
// unaffordable cast, ...). Carries { domain, kind, cardId, reason } so the
// board can tell the player WHY the card bounced instead of a silent revert.
export function onMatchIntentRejected(callback) {
  return on('match:intentRejected', (data) => callback(data));
}

// Ask the server for a fresh per-seat Full. The gap-recovery path the
// ClientMirror invokes when an inbound diff skips a version.
export function requestMatchFull(matchId) {
  if (!isConnected()) return;
  send('match:full:request', { matchId });
}

// Acting-seat envelope provider (solo matches). A solo pilot drives BOTH seats
// over one socket, so every command/intent names its acting seat on the wire
// envelope (`seat: 'p1' | 'p2'`). The server honors it ONLY for the solo
// pilot (actingSeat) and derives the seat from identity for everyone else.
// GameBoard registers a provider on mount when it pilots a solo match; the
// ~40 sendMatchCommand/sendMatchIntent call sites stay untouched.
let matchSeatProvider = null;
export function setMatchSeatProvider(fn) {
  matchSeatProvider = fn || null;
}

function actingSeatFor(payload, kind) {
  const seat = matchSeatProvider?.(payload, kind);
  return seat === 'p1' || seat === 'p2' ? seat : null;
}

// Send a command to the authoritative pipeline. The server reduces it, journals
// it, and broadcasts the resulting diff to every seat. `command` is `{ type,
// payload }`. Dropped silently when offline. The authoritative state is
// re-pulled (Full) on reconnect, so a lost command surfaces as a no-op the
// player simply retries (await-confirm: no optimistic local mutation).
export function sendMatchCommand(matchId, command) {
  if (!isConnected()) return;
  const seat = actingSeatFor(command, 'command');
  send('match:command', { matchId, command, ...(seat ? { seat } : {}) });
}

// Send a combat INTENT to the authoritative pipeline. Combat runs the server's
// stateful CombatEngine (not the pure reducer), so it rides a separate channel
// from `match:command`. `intent` is `{ domain:'combat', kind, ... }`; the server
// resolves it and broadcasts the resulting diff + `match:combat` events. Dropped
// silently when offline (re-pulled via Full on reconnect, like commands).
export function sendMatchIntent(matchId, intent) {
  if (!isConnected()) return;
  const seat = actingSeatFor(intent, 'intent');
  send('match:intent', { matchId, intent, ...(seat ? { seat } : {}) });
}

// Dev-only: stream a local event into the server's match transcript dir. The
// server writes it to .devlogs/<matchId>/client-<seat>.jsonl. No-op offline.
export function sendDevLog(matchId, entry) {
  if (!isConnected()) return;
  const seat = actingSeatFor(entry, 'devlog');
  send('dev:log', { matchId, entry, ...(seat ? { seat } : {}) });
}

export function getSocket() {
  return null;
}

// Re-export the reconnect hook so GameBoard can listen for mid-match
// socket recovery without reaching past socketClient.
export { isConnected, onServerReconnect };
